import { API_BASE_URL, TENANT_SLUG } from '../config';

function withChampionshipQuery(path, championshipId) {
  if (!championshipId) {
    return path;
  }

  const params = new URLSearchParams();
  params.set('campeonato_id', String(championshipId));
  return `${path}?${params.toString()}`;
}

async function request(path, { method = 'GET', token, body } = {}) {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(TENANT_SLUG ? { 'X-Tenant-Slug': TENANT_SLUG } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });

  const payload = await response.json().catch(() => ({}));

  if (!response.ok || payload.success === false) {
    const error = new Error(payload.message || 'Falha na requisição.');
    error.status = response.status;
    error.payload = payload;
    throw error;
  }

  return payload;
}

async function upload(path, { token, file, fieldName = 'image', body = {} } = {}) {
  const formData = new FormData();
  formData.append(fieldName, file);
  Object.entries(body).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      formData.append(key, String(value));
    }
  });

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(TENANT_SLUG ? { 'X-Tenant-Slug': TENANT_SLUG } : {}),
    },
    body: formData,
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload.success === false) {
    const error = new Error(payload.message || 'Falha ao enviar arquivo.');
    error.status = response.status;
    error.payload = payload;
    throw error;
  }

  return payload;
}

export const api = {
  login: (credentials) => request('/auth/login', { method: 'POST', body: credentials }),
  register: (payload) => request('/auth/register', { method: 'POST', body: payload }),
  forgotPassword: (payload) => request('/auth/password/forgot', { method: 'POST', body: payload }),
  resetPassword: (payload) => request('/auth/password/reset', { method: 'POST', body: payload }),
  me: (token) => request('/auth/me', { token }),
  dashboard: (token) => request('/dashboard/admin', { token }),
  users: (token) => request('/users', { token }),
  createUser: (token, body) => request('/users', { method: 'POST', token, body }),
  updateUser: (token, id, body) => request(`/users/${id}`, { method: 'PUT', token, body }),
  deleteUser: (token, id) => request(`/users/${id}`, { method: 'DELETE', token }),
  championships: (token) => request('/campeonatos', { token }),
  championshipDetail: (token, id) => request(`/campeonatos/${id}`, { token }),
  createChampionship: (token, body) => request('/campeonatos', { method: 'POST', token, body }),
  updateChampionship: (token, id, body) => request(`/campeonatos/${id}`, { method: 'PUT', token, body }),
  deleteChampionship: (token, id) => request(`/campeonatos/${id}`, { method: 'DELETE', token }),
  teams: (token) => request('/times', { token }),
  createTeam: (token, body) => request('/times', { method: 'POST', token, body }),
  updateTeam: (token, id, body) => request(`/times/${id}`, { method: 'PUT', token, body }),
  transferTeamResponsibility: (token, id, body) => request(`/times/${id}/responsavel`, { method: 'PUT', token, body }),
  deleteTeam: (token, id) => request(`/times/${id}`, { method: 'DELETE', token }),
  players: (token) => request('/jogadores', { token }),
  createPlayer: (token, body) => request('/jogadores', { method: 'POST', token, body }),
  updatePlayer: (token, id, body) => request(`/jogadores/${id}`, { method: 'PUT', token, body }),
  deletePlayer: (token, id) => request(`/jogadores/${id}`, { method: 'DELETE', token }),
  matches: (token) => request('/jogos', { token }),
  createMatch: (token, body) => request('/jogos', { method: 'POST', token, body }),
  updateMatch: (token, id, body) => request(`/jogos/${id}`, { method: 'PUT', token, body }),
  deleteMatch: (token, id) => request(`/jogos/${id}`, { method: 'DELETE', token }),
  matchDetail: (token, id) => request(`/jogos/${id}`, { token }),
  processMatchReminders: (token) => request('/jogos/lembretes/processar', { method: 'POST', token }),
  smsAlerts: (token) => request('/sms/alertas', { token }),
  sendSmsAlert: (token, body) => request('/sms/alertas', { method: 'POST', token, body }),
  fields: (token) => request('/campos', { token }),
  createField: (token, body) => request('/campos', { method: 'POST', token, body }),
  updateField: (token, id, body) => request(`/campos/${id}`, { method: 'PUT', token, body }),
  deleteField: (token, id) => request(`/campos/${id}`, { method: 'DELETE', token }),
  saveLineups: (token, id, body) => request(`/jogos/${id}/escalacoes`, { method: 'PUT', token, body }),
  matchEvents: (token, id) => request(`/jogos/${id}/eventos`, { token }),
  startMatch: (token, id) => request(`/jogos/${id}/iniciar`, { method: 'POST', token }),
  updateScore: (token, id, body) => request(`/jogos/${id}/placar`, { method: 'PUT', token, body }),
  registerMatchEvent: (token, id, body) => request(`/jogos/${id}/eventos`, { method: 'POST', token, body }),
  finishMatch: (token, id, body) => request(`/jogos/${id}/encerrar`, { method: 'POST', token, body }),
  auditLogs: (token, filters = {}) => {
    const params = new URLSearchParams();
    if (filters.module) params.set('module', filters.module);
    params.set('limit', String(filters.limit || 80));
    return request(`/auditoria?${params.toString()}`, { token });
  },
  ranking: (token, championshipId) => request(withChampionshipQuery('/ranking', championshipId), { token }),
  playerStats: (token, championshipId) => request(withChampionshipQuery('/estatisticas/jogadores', championshipId), { token }),
  teamStats: (token, championshipId) => request(withChampionshipQuery('/estatisticas/times', championshipId), { token }),
  products: (token, filters = {}) => {
    const params = new URLSearchParams();
    params.set('status', filters.status || 'all');
    if (filters.team_id) params.set('team_id', String(filters.team_id));
    if (filters.category_id) params.set('category_id', String(filters.category_id));
    return request(`/produtos?${params.toString()}`, { token });
  },
  productDetail: (token, id) => request(`/produtos/${id}`, { token }),
  createProduct: (token, body) => request('/produtos', { method: 'POST', token, body }),
  updateProduct: (token, id, body) => request(`/produtos/${id}`, { method: 'PUT', token, body }),
  deleteProduct: (token, id) => request(`/produtos/${id}`, { method: 'DELETE', token }),
  coupons: (token, filters = {}) => {
    const params = new URLSearchParams();
    params.set('status', filters.status || 'all');
    return request(`/cupons?${params.toString()}`, { token });
  },
  createCoupon: (token, body) => request('/cupons', { method: 'POST', token, body }),
  updateCoupon: (token, id, body) => request(`/cupons/${id}`, { method: 'PUT', token, body }),
  deleteCoupon: (token, id) => request(`/cupons/${id}`, { method: 'DELETE', token }),
  validateCoupon: (token, body) => request('/cupons/validar', { method: 'POST', token, body }),
  orders: (token, filters = {}) => {
    const params = new URLSearchParams();
    params.set('status', filters.status || 'all');
    params.set('payment_status', filters.payment_status || 'all');
    return request(`/pedidos?${params.toString()}`, { token });
  },
  orderDetail: (token, id) => request(`/pedidos/${id}`, { token }),
  createOrder: (token, body) => request('/pedidos', { method: 'POST', token, body }),
  createOrderPayment: (token, id) => request(`/pedidos/${id}/pagamento`, { method: 'POST', token }),
  updateOrder: (token, id, body) => request(`/pedidos/${id}`, { method: 'PUT', token, body }),
  cancelOrder: (token, id) => request(`/pedidos/${id}`, { method: 'DELETE', token }),
  marketplaceStatus: (token) => request('/marketplace/mercado-pago/status', { token }),
  connectMercadoPago: (token, body) => request('/marketplace/mercado-pago/connect', { method: 'POST', token, body }),
  chatRooms: (token) => request('/chat/salas', { token }),
  journalPosts: (token) => request('/jornal/admin', { token }),
  createJournalPost: (token, body) => request('/jornal', { method: 'POST', token, body }),
  updateJournalPost: (token, id, body) => request(`/jornal/${id}`, { method: 'PUT', token, body }),
  deleteJournalPost: (token, id) => request(`/jornal/${id}`, { method: 'DELETE', token }),
  chatMessages: (token, roomId) => request(`/chat/salas/${roomId}/mensagens`, { token }),
  chatModeration: (token, roomId) => request(`/chat/salas/${roomId}/moderacao`, { token }),
  applyChatModeration: (token, roomId, body) => request(`/chat/salas/${roomId}/moderacao`, { method: 'POST', token, body }),
  removeChatRestriction: (token, restrictionId) => request(`/chat/restricoes/${restrictionId}`, { method: 'DELETE', token }),
  sendChatMessage: (token, roomId, body) => request(`/chat/salas/${roomId}/mensagens`, { method: 'POST', token, body }),
  uploadImage: (token, file, body = {}) => upload('/uploads/imagem', { token, file, body }),
  uploadFile: (token, file, body = {}) => upload('/uploads/arquivo', { token, file, fieldName: 'file', body }),
};
